import {
  Component,
  OnInit,
  Inject,
  Input,
  EventEmitter,
  Output,
  inject,
  OnDestroy
} from '@angular/core';
import {
  UntypedFormGroup,
  FormArray,
  Validators,
  FormGroup,
  FormBuilder,
  AbstractControl
} from '@angular/forms';
import { BaseComponent } from '../../../../components/base/base.component';
import { Bank } from '../../../../models/bank';
import { PeopleService } from '../../../../services/people.service';
import { SharedService } from '../../../../services/shared.service';
import { EmployeeDirectDepositExtended } from '../../../../models/employee-direct-deposit';
import { BankService } from '../../../../services/bank.service';
import { AuthService } from '../../../../services/auth.service';
import { ModalService } from '../../../../services/modal.service';
import { Person } from '../../../../models/person';
import { catchError, map, mergeMap, takeUntil } from 'rxjs/operators';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { InstanceTypes } from '../../../../models/instance-types';
import {
  AccountNumberMatchValidator,
  AllocationValidator,
  NetRemainingValidator,
  RoutingNumberValidator
} from './DirectDepositValidators';
import { ChangeRequestService } from '../../../../services/change-request.service';
import { Store } from '@ngxs/store';
import { ClientSelectionState } from '../../../../state/ClientSelectionState/ClientSelection.state';
import {
  ChangeRequestData,
  ChangeRequestHistoryEntry,
  DirectDepositRequest
} from '../../../../models/change-requests';
import { EmployeeStatus } from '../../../../models/person-status';
import { AclService } from '../../../../services/acl.service';
import {
  ReloadDirectDeposits,
  SetDirectDepositFormDirty,
  TriggerSave
} from '../../../../state/PersonEditorState/PersonEditor.actions';
import { PersonEditorState } from '../../../../state/PersonEditorState/PersonEditor.state';

@Component({
  selector: 'app-payment-details-tab',
  templateUrl: './payment-details-tab.component.html',
  styleUrls: ['./payment-details-tab.component.css'],
  standalone: false
})
export class PaymentDetailsTabComponent extends BaseComponent implements OnInit, OnDestroy {
  readOnly: Observable<boolean>;
  internalReadOnly: boolean = false;

  clientCode: string;
  hasOpenRequest: boolean = false;

  openRequest: ChangeRequestHistoryEntry = null;
  openRequestData: ChangeRequestData<DirectDepositRequest> = null;
  hideAcctNo = false;
  hideAcctNoMatch = false;
  showDisclaimerModal: boolean;

  public ddForm: FormGroup;
  public formIsUnchanged = true;

  private destroy$ = new Subject<void>();

  get isEmployeePortal() {
    return this.env.instanceType == InstanceTypes.EmployeePortal;
  }

  constructor(
    public authService: AuthService,
    private bankService: BankService,
    private formBuilder: FormBuilder,
    private peopleService: PeopleService,
    private routingNumberValidator: RoutingNumberValidator,
    private changeRequestService: ChangeRequestService,
    private store: Store,
    private sharedService: SharedService,
    private aclService: AclService,
    @Inject('env') private env: any
  ) {
    super();
  }

  get activeRecordControls() {
    return this.ddForm.get('enabledAccounts') as FormArray;
  }

  get disabledRecordControls() {
    return this.ddForm.get('disabledAccounts') as FormArray;
  }

  get percentOfNetTotal() {
    let total = 0;
    const array: any[] = this.activeRecordControls.value;
    array
      .filter(x => x.amountType == 1)
      .forEach(item => {
        total += item.amount;
      });
    return total;
  }

  get countOfNetRemaining() {
    const array: any[] = this.activeRecordControls.value;
    return array.filter(x => x.amountType == 2).length;
  }

  isPersonActive(): Observable<boolean> {
    return this.store.selectOnce(PersonEditorState.getEmployeeStatuses).pipe(
      map(statuses => {
        const sorted = structuredClone(statuses).sort((a: EmployeeStatus, b: EmployeeStatus) => {
          return new Date(b.DateStatusChange).getTime() - new Date(a.DateStatusChange).getTime();
        });
        if (sorted.length < 1) return false;
        return sorted[0].StatusDescription == 'Active';
      })
    );
  }
  ngOnInit() {
    this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
      this.clientCode = code;
      this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
        if (person.Id == null) {
          this.store
            .dispatch(
              new TriggerSave(
                this.clientCode,
                {
                  1: 'personal-details-tab',
                  2: 'job-details-tab',
                  3: 'earnings-and-deductions-tab',
                  4: 'taxes-tab',
                  5: 'benefits-tab',
                  6: 'direct-deposit-tab',
                  7: 'audit-log-tab'
                },
                'direct-deposit-tab'
              )
            )
            .subscribe(() => {
              this.init();
            });
        } else {
          this.init();
        }
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  init() {
    this.readOnly = this.store.select(PersonEditorState.getReadOnly).pipe(
      takeUntil(this.destroy$),
      map(x => x || this.internalReadOnly)
    );
    this.isPersonActive().subscribe(isActive => {
      this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
        this.showDisclaimerModal = false;
        if (this.isEmployeePortal) {
          this._checkForChangeRequests(person.Id, isActive);
        }
        if (!this.isEmployeePortal) {
          this.aclService
            .CurrentUserAccessCheck('Client Portal Admin', 'directDepositChange', this.clientCode)
            .subscribe(acl => {
              if (acl) {
                this._checkForChangeRequests(person.Id, isActive);
              } else {
                this.buildDDForm();
              }
            });
        }
      });
    });
  }

  buildDDForm() {
    this.store.selectOnce(PersonEditorState.getEmployeeDirectDeposits).subscribe(directDeposits => {
      this.store.dispatch(new SetDirectDepositFormDirty(false));
      const activeRecords: FormGroup[] = [];
      const disabledRecords: FormGroup[] = [];
      this.formIsUnchanged = true;
      directDeposits.forEach(x => {
        if (x.IsActive) {
          const group = this.formBuilder.group({
            id: this.formBuilder.control(x.Id),
            bankName: this.formBuilder.control(x.BankName),
            routingNumber: this.formBuilder.control(x.RoutingNumber),
            accountNumber: this.formBuilder.control(x.AccountNumber),
            accountType: this.formBuilder.control(x.AccountType),
            amountType: this.formBuilder.control(x.CalculationMethod, Validators.required),
            amount: this.formBuilder.control(x.Amount),
            deactivated: this.formBuilder.control(false)
          });
          if (x.CalculationMethod == 2) {
            //net remaining
            group.get('amount').setValue(0);
            group.get('amount').setValidators(null);
          } else if (x.CalculationMethod == 1) {
            // net percent
            group.get('amount').setValidators([Validators.required, Validators.max(100.0)]);
          } else {
            group.get('amount').setValidators([Validators.required]);
          }

          group.get('amountType').valueChanges.subscribe(val => {
            if (val == 2) {
              //net remaining
              group.get('amount').setValue(0);
              group.get('amount').setValidators(null);
            } else if (val == 1) {
              // net percent
              group.get('amount').setValidators([Validators.required, Validators.max(100.0)]);
            } else {
              group.get('amount').setValidators([Validators.required]);
            }
            group.get('amount').updateValueAndValidity();
          });
          activeRecords.push(group);
        } else {
          const group = this.formBuilder.group({
            id: this.formBuilder.control(x.Id),
            bankName: this.formBuilder.control(x.BankName),
            routingNumber: this.formBuilder.control(x.RoutingNumber),
            accountNumber: this.formBuilder.control(x.AccountNumber),
            accountType: this.formBuilder.control(x.AccountType),
            amountType: this.formBuilder.control(x.CalculationMethod),
            amount: this.formBuilder.control(x.Amount),
            deactivated: this.formBuilder.control(false)
          });
          disabledRecords.push(group);
          group.get('amountType').valueChanges.subscribe(val => {
            if (val == 2) {
              //net remaining
              group.get('amount').setValue(0);
              group.get('amount').setValidators(null);
            } else if (val == 1) {
              // net percent
              group.get('amount').setValidators([Validators.required, Validators.max(100.0)]);
            } else {
              group.get('amount').setValidators([Validators.required]);
            }
          });
        }
      });
      this.ddForm = this.formBuilder.group({
        enabledAccounts: this.formBuilder.array(activeRecords, [
          AllocationValidator,
          NetRemainingValidator
        ]),
        disabledAccounts: this.formBuilder.array(disabledRecords)
      });
      this.ddForm.valueChanges
        .pipe(
          mergeMap(val => {
            return this.store.selectOnce(PersonEditorState.getEmployeeDirectDeposits).pipe(
              map(directDeposits => {
                return {
                  val,
                  directDeposits
                };
              })
            );
          })
        )
        .subscribe(val => {
          let differenceFound = false;
          for (let i = 0; i < val.val.enabledAccounts.length; i++) {
            const control = val.val.enabledAccounts[i];
            if (control.id == null) {
              //We have a new record.
              //This means we can stop searching here.
              differenceFound = true;
              break;
            }
            const existing = val.directDeposits.find(y => y.Id == control.id);
            if (
              !existing.IsActive ||
              existing.CalculationMethod !== control.amountType ||
              existing.Amount !== control.amount
            ) {
              //difference found.
              differenceFound = true;
              break;
            }
          }
          if (!differenceFound) {
            //still haven't found a difference. Check the disabled ones.
            for (let i = 0; i < val.val.disabledAccounts.length; i++) {
              const control = val.val.disabledAccounts[i];
              const existing = val.directDeposits.find(y => y.Id == control.id);
              if (existing.IsActive) {
                //Found a difference.
                differenceFound = true;
                break;
              }
            }
          }
          this.formIsUnchanged = !differenceFound;
          this.store.dispatch(new SetDirectDepositFormDirty(differenceFound));
        });
    });
  }

  cancelRequest() {
    this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
      this.ddForm = null;
      this.changeRequestService
        .cancelOpenDirectDepositRequest(this.clientCode, person.Id)
        .subscribe(_ => {
          this.hasOpenRequest = false;
          this.buildDDForm();
        });
    });
  }

  closeModal() {
    this.openRequest = null;
    this.openRequestData = null;
    if (!this.isEmployeePortal) {
      this.ddForm = null;
      this.store.dispatch(new ReloadDirectDeposits()).subscribe(() => {
        this.init();
      });
    }
  }

  blurAcctNo(index: number) {
    const group = this.activeRecordControls.at(index) as FormGroup;
    group.get('accountNumberMask').setValue(true);
  }
  blurAcctNoMatch(index: number) {
    const group = this.activeRecordControls.at(index) as FormGroup;
    group.get('accountNumberMatchMask').setValue(true);
  }
  preSubmissionChecks() {
    if (this.isEmployeePortal) {
      this.showDisclaimerModal = true;
    } else {
      this.formSubmit();
    }
  }

  disclaimerClosed(result: boolean) {
    if (result) {
      this.formSubmit();
    }
    this.showDisclaimerModal = false;
  }

  formSubmit() {
    this.store.selectOnce(PersonEditorState.getEmployeeDirectDeposits).subscribe(directDeposits => {
      this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
        this.sharedService.personEditsSaved.next();
        const formValue = this.ddForm.value;
        const final: EmployeeDirectDepositExtended[] = [];
        formValue.enabledAccounts.forEach(acct => {
          if (acct.id != null) {
            const ddItem = structuredClone(directDeposits.find(x => x.Id == acct.id));
            ddItem.CalculationMethod = acct.amountType;
            ddItem.Amount = acct.amount;
            ddItem.IsActive = true;
            final.push(ddItem);
          } else {
            const ddItem = new EmployeeDirectDepositExtended();
            ddItem.DateAdded = new Date();
            ddItem.RoutingNumber = acct.routingNumber;
            ddItem.AccountNumber = acct.accountNumber;
            ddItem.CalculationMethod = acct.amountType;
            ddItem.AccountType = acct.accountType;
            ddItem.IsPrenote = false;
            ddItem.IsActive = true;
            ddItem.BankName = acct.bankName;
            ddItem.Amount = acct.amount;
            final.push(ddItem);
          }
        });
        formValue.disabledAccounts.forEach(acct => {
          if (acct.id != null) {
            const ddItem = structuredClone(directDeposits.find(x => x.Id == acct.id));
            ddItem.IsActive = false;
            final.push(ddItem);
          }
        });
        if (!this.isEmployeePortal) {
          this.ddForm = null;
          this.peopleService.setDirectDeposits(this.clientCode, person.Id, final).subscribe(_ => {
            this.store.dispatch(new ReloadDirectDeposits()).subscribe(() => {
              this.buildDDForm();
            });
          });
        } else {
          this.ddForm = null;
          this.changeRequestService
            .submitDirectDepositChangeRequest(this.clientCode, person.Id, final)
            .subscribe(_ => {
              this.store.dispatch(new ReloadDirectDeposits()).subscribe(() => {
                this.init();
              });
            });
        }
      });
    });
  }

  cancelChanges() {
    this.ddForm = null;
    this.store.dispatch(new ReloadDirectDeposits()).subscribe(() => {
      this.init();
    });
  }

  addNewAccount() {
    const formArray = this.ddForm.get('enabledAccounts') as FormArray;
    const newFormGroup = this.formBuilder.group(
      {
        id: this.formBuilder.control(null),
        bankName: this.formBuilder.control(null),
        routingNumber: this.formBuilder.control(
          null,
          [Validators.required, Validators.minLength(9), Validators.maxLength(9)],
          [this.routingNumberValidator.validate.bind(this.routingNumberValidator)]
        ),
        accountNumber: this.formBuilder.control(null, [
          Validators.required,
          Validators.minLength(4)
        ]),
        accountNumberMatch: this.formBuilder.control(null, Validators.required),
        accountNumberMask: this.formBuilder.control(false),
        accountNumberMatchMask: this.formBuilder.control(false),
        accountType: this.formBuilder.control(null, Validators.required),
        amountType: this.formBuilder.control(null, Validators.required),
        amount: this.formBuilder.control(null)
      },
      {
        validators: [AccountNumberMatchValidator]
      }
    );

    newFormGroup.get('accountNumber').valueChanges.subscribe(_ => {
      newFormGroup.get('accountNumberMask').setValue(false);
    });
    newFormGroup.get('accountNumberMatch').valueChanges.subscribe(_ => {
      newFormGroup.get('accountNumberMatchMask').setValue(false);
    });

    newFormGroup.get('amountType').valueChanges.subscribe(val => {
      if (val == 2) {
        //net remaining
        newFormGroup.get('amount').setValue(0);
        newFormGroup.get('amount').setValidators(null);
      } else if (val == 1) {
        // net percent
        newFormGroup.get('amount').setValidators([Validators.required, Validators.max(100.0)]);
      } else {
        newFormGroup.get('amount').setValidators([Validators.required]);
      }
      newFormGroup.get('amount').updateValueAndValidity();
    });
    newFormGroup.get('routingNumber').valueChanges.subscribe(val => {
      if (val && val.length === 9) {
        this.bankService
          .getBank(val)
          .pipe(
            catchError(err => {
              const bank: Bank = null;
              return of(bank);
            })
          )
          .subscribe(bank => {
            newFormGroup.get('bankName').setValue(bank?.DisplayName ?? '');
          });
      }
    });
    formArray.push(newFormGroup);

    this.ddForm.markAsDirty();
  }

  disableAccount(item: AbstractControl, index: number) {
    this.activeRecordControls.removeAt(index);
    if (item.get('id').value != null) {
      item.get('deactivated').setValue(true);
      this.disabledRecordControls.push(item);
    }
    this.ddForm.markAsDirty();
  }
  enableAccount(item: AbstractControl, index: number) {
    this.disabledRecordControls.removeAt(index);
    item.get('amountType').setValue(0);
    item.get('amount').setValue(0);

    this.activeRecordControls.push(item);
    this.ddForm.markAsDirty();
  }

  openModal() {
    this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
      const obs: Observable<ChangeRequestHistoryEntry | ChangeRequestData<DirectDepositRequest>>[] =
        [
          this.changeRequestService.getOpenDirectDepositRequest(
            this.clientCode,
            person.Id,
            this.isEmployeePortal
          ),
          this.changeRequestService.getDirectDepositChangeData(
            this.clientCode,
            person.Id,
            this.isEmployeePortal
          )
        ];
      forkJoin(obs).subscribe((result: any[]) => {
        this.openRequest = result[0];
        this.openRequestData = result[1];
      });
    });
  }

  modalCancelRequest() {
    this.closeModal();
    this.cancelRequest();
  }

  private _checkForChangeRequests(personId: number, isActive: boolean) {
    this.changeRequestService.canEnableChangeRequests(this.clientCode).subscribe(y => {
      if (y.Value && isActive) {
        this.changeRequestService
          .hasDirectDepositRequest(this.clientCode, personId, this.isEmployeePortal)
          .subscribe(result => {
            this.hasOpenRequest = result.Value;
            this.buildDDForm();
          });
      } else {
        this.internalReadOnly = this.isEmployeePortal;
        this.buildDDForm();
      }
    });
  }
}
